"use client";

import Link from "next/link";
import { useMemo } from "react";

import Carousel from "components/Carousels";
import { IInitialOtherFee, IMonthlyOtherFee } from "types/fee";
import { formatDisplayMonths } from "utils/formatDisplayMonths.util";
import { sortFeesByAscendingOrder } from "utils/sortFee.util";
import { convertNumberToLocaleString } from "utils/string.util";

const ApplicationParkingCard = ({
  parkingId,
  spaceGroupId,
  parkingName,
  parkingAddress,
  parkingControlNumber,
  monthlyRent,
  deposit,
  gratuityFee,
  supportCarModal,
  numberOfFree,
  numberOfFullSpace,
  parkingImages,
  administrativeFee,
  renewalFee,
  renewalAdminFee,
  storeFee,
  monthlyOtherFees = [],
  initialOtherFees = [],
}: {
  parkingId: string | null;
  spaceGroupId: string;
  parkingName: string;
  parkingAddress: string;
  parkingImages: string[];
  parkingControlNumber: string;
  monthlyRent: number;
  deposit: number;
  gratuityFee: number;
  supportCarModal: string;
  numberOfFree: number;
  numberOfFullSpace: number;
  administrativeFee: number;
  renewalFee: number;
  renewalAdminFee: number;
  storeFee: number;
  monthlyOtherFees?: IMonthlyOtherFee[];
  initialOtherFees?: IInitialOtherFee[];
}) => {
  const sortedMonthlyOtherFees = useMemo(() => sortFeesByAscendingOrder(monthlyOtherFees), [monthlyOtherFees]);
  const sortedInitialOtherFees = useMemo(() => sortFeesByAscendingOrder(initialOtherFees), [initialOtherFees]);

  return (
    <div className="flex h-fit w-full flex-col items-center justify-center bg-white p-4">
      <div className="w-[268px]">
        <div className="relative h-[150px] w-[268px]">
          <Carousel imageUrls={parkingImages} />
        </div>
        <div className="my-4 flex flex-col items-center gap-4">
          <span className="start text-20px">{parkingName}</span>
        </div>
        <div className="flex w-full flex-col gap-2 border-b px-2 pb-4 text-s">
          <div className="flex grow-0 pt-1">
            <span className="w-14 pt-1 text-12px text-gray">住所</span>
            <span className="w-[194px]">{parkingAddress}</span>
          </div>
          <div className="flex items-center">
            <span className="w-14 text-12px text-gray">物件No.</span>
            <span className="w-[194px]">{parkingControlNumber}</span>
          </div>
        </div>
        <div className="flex flex-col border-b px-2 pb-4 pt-5">
          <span className="mb-4 text-[19.2px]">区画</span>
          <span className="mb-2 text-xs font-bold text-gray">月額費用</span>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-gray">賃料</p>
            <p className="text-s">{convertNumberToLocaleString(monthlyRent)}</p>
          </div>
          {sortedMonthlyOtherFees.length > 0 && (
            <div className="space-y-2">
              {sortedMonthlyOtherFees.map((fee, index) => (
                <div key={fee.id || index} className="flex items-center justify-between gap-1">
                  <div className="text-gray">
                    <p className="text-xs leading-[1.5]">{fee.name}</p>
                    <p className="text-xxs leading-[1.5]">{formatDisplayMonths(fee.applyMonths)}</p>
                  </div>
                  <p className="shrink-0 text-s">{convertNumberToLocaleString(fee.cost.value)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col border-b px-2 py-4">
          <span className="mb-2 text-xs font-bold text-gray">初期費用</span>
          <div className="mb-2 flex items-center justify-between text-s">
            <p className="text-xs text-gray">敷金</p>
            <p>{convertNumberToLocaleString(deposit)}</p>
          </div>
          <div className="mb-2 flex items-center justify-between text-s">
            <p className="text-xs text-gray">礼金</p>
            <p>{convertNumberToLocaleString(gratuityFee)}</p>
          </div>
          <div className="mb-2 flex items-center justify-between text-s">
            <p className="text-xs text-gray">事務手数料</p>
            <p>{convertNumberToLocaleString(administrativeFee)}</p>
          </div>
          {sortedInitialOtherFees.length > 0 && (
            <div className="space-y-2">
              {sortedInitialOtherFees.map((fee, index) => (
                <div key={fee.id || index} className="flex items-center justify-between gap-1">
                  <p className="text-xs text-gray">{fee.name}</p>
                  <p className="shrink-0 text-s">{convertNumberToLocaleString(fee.cost.value)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col border-b px-2 py-4">
          <span className="mb-2 text-xs font-bold text-gray">更新時費用</span>
          <div className="mb-2 flex items-center justify-between text-s">
            <p className="text-xs text-gray">更新料</p>
            <p>{convertNumberToLocaleString(renewalFee)}</p>
          </div>
          <div className="mb-2 flex items-center justify-between text-s">
            <p className="text-xs text-gray">更新事務手数料</p>
            <p>{convertNumberToLocaleString(renewalAdminFee)}</p>
          </div>
          <div className="mb-2 flex items-center justify-between text-s">
            <p className="text-xs text-gray">保管場所使用承諾証明書発行料</p>
            {/* Administrative fee */}
            <p>{convertNumberToLocaleString(storeFee)}</p>
          </div>
        </div>
        <div className="flex flex-col border-b px-2 py-4 text-xs">
          <div className="flex items-center justify-between">
            <p className="min-w-[48px] font-bold text-gray">対応車両</p>
            <p>{supportCarModal}</p>
          </div>
        </div>
        <p className="mb-4 mt-2 rounded text-xs text-gray">※ 申込受付時点の情報</p>
        <div className="bg-default p-4 text-xs leading-[1.5] text-gray">
          <div className="flex items-center justify-between">
            <p className="font-bold text-gray">利用状況</p>
            {parkingId != null && (
              <Link
                className="text-primary hover:text-linkDark"
                href={`/property/${parkingId}/space-group/${spaceGroupId}`}
              >
                利用状況を確認
              </Link>
            )}
          </div>
          <div className="mt-2 flex flex-col bg-white p-4">
            <div className="flex items-center gap-2 border-b pb-2">
              <p>空車</p>
              <p className="text-s font-bold text-blue">{`${numberOfFree}台`}</p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <p>満車</p>
              <p className="text-s font-bold text-full">{`${numberOfFullSpace}台`}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationParkingCard;
